/** Guarded analytics. The widget/page emit Segment-style events only when the
 * host has loaded an analytics provider — feature-detected and wrapped so a
 * missing or throwing provider can never break the sign-in flow. No cookie
 * banner here: the host context decides whether analytics is loaded.
 */

/** How long to wait for the provider to acknowledge an event before giving up on
 * it. A visitor must never be held behind a slow or silent analytics provider,
 * so this is short and always fires. */
const ACK_TIMEOUT_MS = 500;

declare global {
  interface Window {
    analytics?: {
      track?: (
        event: string,
        props?: Record<string, unknown>,
        options?: Record<string, unknown>,
        callback?: () => void,
      ) => void;
    };
  }
}

/** Emit an event, resolving once the provider has taken it (or after
 * ACK_TIMEOUT_MS, or immediately when there is no provider).
 *
 * Await it before navigating away — a full page load cancels a request the
 * provider hasn't sent yet, which silently drops the last event of a funnel.
 * Fire-and-forget callers can ignore the promise; it never rejects.
 */
export function track(
  event: string,
  props?: Record<string, unknown>,
): Promise<void> {
  return new Promise((resolve) => {
    try {
      const analytics = typeof window !== "undefined" ? window.analytics : undefined;
      if (!analytics || typeof analytics.track !== "function") {
        resolve();
        return;
      }
      // Whichever comes first: the provider's callback, or the timeout.
      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        resolve();
      };
      analytics.track(event, props, {}, settle);
      setTimeout(settle, ACK_TIMEOUT_MS);
    } catch {
      /* analytics must never break the flow */
      resolve();
    }
  });
}

/** The funnel context bound to a tracker and attached to every event it fires. */
type EventContext = { source?: string; variant?: string };

/** The props for a single event, beyond the bound context. The context keys are
 * reserved — set once via `trackWith`, never per call — so the type forbids
 * them, and the context can't be overridden by accident. */
export type EventProps = Record<string, unknown> & {
  [K in keyof EventContext]?: never;
};

export type Tracker = (event: string, props?: EventProps) => Promise<void>;

/** Bind a funnel's context (source + variant) once, then attach it to every
 * event the returned tracker fires — so the whole funnel is attributable
 * without repeating, and forgetting, the context at each call.
 *
 * The context is *authoritative*: merged after the per-call props, so nothing a
 * call passes can override it (and the types reserve its keys, keeping them out
 * of per-call props to begin with). Context keys whose value is `undefined` are
 * dropped, so an absent variant emits no `variant` key at all; only the context
 * is pruned, so a deliberately-absent per-call value (a `status`, say) survives.
 */
export function trackWith(context: EventContext): Tracker {
  const base = pruneUndefined(context);
  return (event, props) => track(event, props ? { ...props, ...base } : base);
}

function pruneUndefined(
  props: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}
